import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import styles from "./edituser.module.css";
import axios from "axios";
import {
  ArrowRight,
  EyeFill,
  EyeSlashFill,
  KeyFill,
} from "react-bootstrap-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Loading from "../../../Components/Loading/Loading";
import toast from "react-hot-toast";
import generator from "generate-password-browser";
import { LoadingButton } from "@mui/lab";

function EditUser() {
  // utils
  const theme = useTheme();
  const location = useLocation();
  const rowData = location.state;
  const [initialLoading, setInitialLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Data fields
  const [name, setName] = useState("");
  const [phoneNum, setPhoneNum] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [userRole, setUserRole] = useState("");
  const [worktime, setWorktime] = useState("");
  const [sites, setSites] = useState([]);
  const [availableWorkTimes, setAvailableWorkTimes] = useState([]);
  const [availableSites, setAvailableSites] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showPassword, setShowPassword] = React.useState(false);
  const checkRole = useSelector((state) => state.auth.role.roleName);

  const fetchData = useCallback(async () => {
    setInitialLoading(true);
    try {
      const [WorkTime, roles, site] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/worktime/`, {
          withCredentials: true,
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/roles/`, {
          withCredentials: true,
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/sites`, {
          withCredentials: true,
        }),
      ]);

      setAvailableWorkTimes(WorkTime.data);
      setAvailableRoles(roles.data);
      setAvailableSites(site.data);
      setInitialLoading(false);
    } catch (error) {
      setInitialLoading(false);
      toast.error(error.message || "Failed to fetch data");
    }
  }, []);

  useEffect(() => {
    setName(rowData.name || "");
    setPhoneNum(rowData.phoneNumber || "");
    setUsername(rowData.username || "");
    setPassword(rowData.password || "");
    setUserRole(rowData.Role?.name || "");
    setWorktime(rowData.WorkTime?.duration || "");
    setSites(rowData.Sites.map((s) => s.name) || []);
    fetchData();
  }, [fetchData, rowData]);

  const filteredRoles =
    checkRole === "Admin"
      ? availableRoles.filter((role) => role.name === "Employee")
      : availableRoles;

  function getEntityID(data, entity) {
    const found = data.find((e) => e.name === entity);
    return found ? found.id : null;
  }
  function getWorkTimeId(data, entity) {
    const found = data.find((e) => e.duration === entity);
    return found ? found.id : null;
  }

  const handleNameChange = (e) => {
    setName(e.target.value);
  };
  const handlePhoneNumChange = (e) => {
    setPhoneNum(e.target.value);
  };
  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
  };
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleWorkTimeChange = (e) => {
    setWorktime(e.target.value);
  };
  const handleRoleChange = (e) => {
    const role = e.target.value;
    if (role !== "SuperAdmin") {
      setSites((prev) => {
        if (Array.isArray(prev)) {
          if (prev.length > 0) {
            return prev[0];
          } else {
            return "";
          }
        } else {
          return prev;
        }
      });
    } else {
      setSites((prev) => {
        if (prev === "") {
          return [];
        }
        return [prev];
      });
    }
    setUserRole(role);
  };
  const handleSiteChange = (e) => {
    setSites(e.target.value);
  };
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const handleSubmit = async () => {
    setLoading(true);
    // Reset errors
    const newErrors = {};

    // Validation checks
    if (!name.trim()) newErrors.name = "الإسم مطلوب.";
    if (!phoneNum.trim() || !/^\d+$/.test(phoneNum))
      newErrors.phoneNum = "رقم الهاتف مطلوب.";
    if (!username.trim()) newErrors.username = "إسم المستخدم مطلوب.";
    if (!password.trim() || password.length < 6)
      newErrors.password = "كلمة السر يجب أن تكون على الأقل ستة أحرف.";
    if (!userRole) newErrors.userRole = "دور الموظف مطلوب.";
    if (!worktime) newErrors.worktime = "مدة العمل مطلوبة.";
    if (!sites.length) newErrors.sites = "الموقع مطلوب.";

    // If there are errors, set them and prevent submission
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const sitesIds = Array.isArray(sites)
      ? sites.map((site) => getEntityID(availableSites, site))
      : [getEntityID(availableSites, sites)];
    const editedUser = {
      name: name,
      phoneNumber: phoneNum,
      username: username,
      password: password,
      RoleId: getEntityID(availableRoles, userRole),
      WorkTimeId: getWorkTimeId(availableWorkTimes, worktime),
      SiteIds: sitesIds,
    };
    setErrors({});

    const oldSitesId = rowData.Sites.map((site) => site.id);

    if (
      editedUser.name === rowData.name &&
      editedUser.phoneNumber === rowData.phoneNumber &&
      editedUser.username === rowData.username &&
      editedUser.password === rowData.password &&
      editedUser.RoleId === rowData.RoleId &&
      editedUser.WorkTimeId === rowData.WorkTimeId &&
      JSON.stringify(editedUser.SiteIds) === JSON.stringify(oldSitesId)
    ) {
      setLoading(false);
      toast.error("أنت لم تقم بتعديل أي شيء.");
      return;
    }
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/user/${rowData.id}`,
        editedUser,
        {
          withCredentials: true,
        }
      );
      if (res) {
        setLoading(false);
        navigate("/app/users");
        toast.success("تم تعديل المستخدم بنجاح!");
      }
    } catch (error) {
      setLoading(false);
      toast.error(error);
    }
  };

  const generatePassword = () => {
    let generatedPassword = generator.generate({
      length: 12,
      numbers: true,
      symbols: true,
      uppercase: true,
      lowercase: true,
      strict: true, // Ensures at least one character of each type
    });
    setPassword(generatedPassword);
  };
  const navigate = useNavigate();
  return initialLoading ? (
    <Loading />
  ) : (
    <div>
      <div className={styles.form}>
        <div className={styles.container}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              onClick={() => navigate(-1)}
              variant="contained"
              sx={{
                width: "48px",
                height: "48px",
                padding: 0,
                margin: 0,
                minWidth: "auto",
                marginRight: "12px",
              }}
            >
              <ArrowRight size={24} />
            </Button>
            <Typography
              variant="h4"
              color={theme.palette.neutral[900]}
              fontWeight={700}
            >
              تعديل مستخدم
            </Typography>
          </div>
          <div className={styles.basicDetails}>
            <div className={styles.formSection}>
              <Typography
                variant="h5"
                color={theme.palette.neutral[800]}
                fontWeight={600}
                marginBottom={2}
              >
                المعلومات الأساسية
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={(2, 3)}>
                {/* View Quantity Field */}
                <FormControl
                  fullWidth
                  sx={{ flex: "1 1 45%", minWidth: "250px" }}
                >
                  <TextField
                    fullWidth
                    label="الإسم"
                    type="text"
                    value={name}
                    onChange={handleNameChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    slotProps={{
                      htmlInput: {
                        maxLength: 24,
                      },
                      input: {
                        endAdornment: name && (
                          <InputAdornment position="end">
                            {name.length}/{24}
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </FormControl>

                {/* View Type Field */}
                <FormControl
                  fullWidth
                  sx={{ flex: "1 1 45%", minWidth: "250px" }}
                >
                  <TextField
                    fullWidth
                    label="رقم الهاتف"
                    type="tel"
                    value={phoneNum}
                    onChange={handlePhoneNumChange}
                    error={!!errors.phoneNum}
                    helperText={errors.phoneNum}
                  />
                </FormControl>
              </Box>
            </div>
            <div className={styles.formSection}>
              <Typography
                variant="h5"
                color={theme.palette.neutral[800]}
                fontWeight={600}
                marginBottom={2}
              >
                حساب المستخدم
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={(2, 3)}>
                {/* View Quantity Field */}
                <FormControl
                  fullWidth
                  sx={{ flex: "1 1 45%", minWidth: "250px" }}
                >
                  <TextField
                    fullWidth
                    label="اسم المستخدم"
                    type="text"
                    value={username}
                    onChange={handleUsernameChange}
                    error={!!errors.username}
                    helperText={errors.username}
                    slotProps={{
                      htmlInput: {
                        maxLength: 24,
                      },
                      input: {
                        endAdornment: username && (
                          <InputAdornment position="end">
                            {username.length}/{24}
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </FormControl>
                <FormControl
                  sx={{ flex: "1 1 45%", minWidth: "250px" }}
                  error={!!errors.password}
                  variant="outlined"
                >
                  <InputLabel htmlFor="outlined-adornment-password">
                    كلمة السر
                  </InputLabel>
                  <OutlinedInput
                    id="outlined-adornment-password"
                    type={showPassword ? "text" : "password"}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={"Generate Password"}
                          onClick={generatePassword}
                          edge="end"
                        >
                          <KeyFill />
                        </IconButton>
                        <IconButton
                          aria-label={
                            showPassword
                              ? "hide the password"
                              : "display the password"
                          }
                          onClick={handleClickShowPassword}
                          edge="end"
                          sx={{ marginLeft: "8px" }}
                        >
                          {showPassword ? <EyeSlashFill /> : <EyeFill />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Password"
                    value={password}
                    onChange={handlePasswordChange}
                  />
                  <FormHelperText>{errors.password}</FormHelperText>
                </FormControl>
              </Box>
            </div>
            <div className={styles.formSection}>
              <Typography
                variant="h5"
                color={theme.palette.neutral[800]}
                fontWeight={600}
                marginBottom={2}
              >
                معلومات العمل
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={(2, 3)}>
                {/* View Type Field */}
                <FormControl
                  fullWidth
                  sx={{ flex: "1 1 45%", minWidth: "250px" }}
                  error={!!errors.userRole}
                >
                  <InputLabel id="role-label">دور المستخدم</InputLabel>
                  <Select
                    fullWidth
                    labelId="role-label"
                    label="دور المستخدم"
                    value={userRole}
                    onChange={handleRoleChange}
                  >
                    {availableRoles.length > 0 ? (
                      filteredRoles.map((role, index) => (
                        <MenuItem key={index} value={role.name}>
                          {role.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>
                        لا يوجد خيارات
                      </MenuItem>
                    )}
                  </Select>
                  <FormHelperText>{errors.userRole}</FormHelperText>
                </FormControl>
                {/* View Quantity Field */}

                <FormControl
                  fullWidth
                  sx={{ flex: "1 1 45%", minWidth: "250px" }}
                  error={!!errors.worktime}
                >
                  <InputLabel id="worktime-label">مدة عمل المستخدم</InputLabel>
                  <Select
                    fullWidth
                    labelId="worktime-label" // Make sure this matches the InputLabel's id
                    label="مدة عمل المستخدم"
                    value={worktime}
                    onChange={handleWorkTimeChange}
                  >
                    {availableWorkTimes.length > 0 ? (
                      availableWorkTimes.map((worktime, index) => (
                        <MenuItem key={index} value={worktime.duration}>
                          {worktime.duration}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>
                        لا يوجد خيارات
                      </MenuItem>
                    )}
                  </Select>
                  <FormHelperText>{errors.worktime}</FormHelperText>
                </FormControl>

                {/* Date & Time Picker */}
                <FormControl
                  sx={{ flex: "1 1 45%", minWidth: "250px" }}
                  error={!!errors.sites}
                >
                  <InputLabel id="demo-multiple-chip-label">
                    موقع عمل المستخدم
                  </InputLabel>
                  <Select
                    labelId="demo-multiple-chip-label"
                    id="demo-multiple-chip"
                    multiple={
                      checkRole === "Admin" || userRole === "Employee"
                        ? false
                        : true
                    }
                    value={sites}
                    onChange={handleSiteChange}
                    input={
                      <OutlinedInput
                        id="select-multiple-chip"
                        label="موقع عمل المستخدم"
                      />
                    }
                    renderValue={(selected) => {
                      const selectedValues = Array.isArray(selected)
                        ? selected
                        : [selected];
                      return (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selectedValues.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      );
                    }}
                    MenuProps={MenuProps}
                  >
                    {availableSites.length > 0 ? (
                      availableSites.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          {site.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>
                        لا يوجد خيارات
                      </MenuItem>
                    )}
                  </Select>
                  <FormHelperText>{errors.sites}</FormHelperText>
                </FormControl>
              </Box>
            </div>
          </div>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={loading}
            sx={{ fontSize: "20px" }}
            fullWidth
            onClick={handleSubmit}
          >
            تعديل
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}

export default EditUser;
